import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiArrowDown, FiGithub, FiLinkedin, FiMessageCircle } from 'react-icons/fi';
import { useTheme } from '../contexts/ThemeContext';

const HeroSection = styled.section<{ $isDarkMode: boolean }>`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  padding: 0 2rem;
  background: ${props => props.$isDarkMode 
    ? 'linear-gradient(135deg, #0f0f23 0%, #1a1a2e 50%, #16213e 100%)'
    : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 50%, #cbd5e1 100%)'
  };

  @media (max-width: 768px) {
    padding: 0 1rem;
    min-height: 90vh;
  }
`;

const HeroBackground = styled.div<{ $isDarkMode: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: ${props => props.$isDarkMode 
    ? `
      radial-gradient(circle at 30% 30%, rgba(255, 255, 255, 0.1) 0%, transparent 40%),
      radial-gradient(circle at 70% 70%, rgba(139, 92, 246, 0.08) 0%, transparent 45%),
      radial-gradient(circle at 20% 80%, rgba(236, 72, 153, 0.06) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(245, 158, 11, 0.05) 0%, transparent 40%),
      linear-gradient(135deg, rgba(255, 255, 255, 0.02) 0%, transparent 50%),
      linear-gradient(45deg, rgba(139, 92, 246, 0.03) 0%, transparent 50%)
    `
    : `
      radial-gradient(circle at 30% 30%, rgba(139, 92, 246, 0.05) 0%, transparent 40%),
      radial-gradient(circle at 70% 70%, rgba(236, 72, 153, 0.04) 0%, transparent 45%),
      radial-gradient(circle at 20% 80%, rgba(245, 158, 11, 0.03) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(34, 197, 94, 0.03) 0%, transparent 40%),
      linear-gradient(135deg, rgba(139, 92, 246, 0.02) 0%, transparent 50%),
      linear-gradient(45deg, rgba(236, 72, 153, 0.02) 0%, transparent 50%)
    `
  };
  z-index: 1;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: ${props => props.$isDarkMode 
      ? `
        repeating-linear-gradient(
          45deg,
          transparent,
          transparent 2px,
          rgba(255, 255, 255, 0.01) 2px,
          rgba(255, 255, 255, 0.01) 4px
        )
      `
      : `
        repeating-linear-gradient(
          45deg,
          transparent,
          transparent 2px,
          rgba(139, 92, 246, 0.01) 2px,
          rgba(139, 92, 246, 0.01) 4px
        )
      `
    };
    pointer-events: none;
  }
`;

const HeroContent = styled.div`
  max-width: 1200px;
  text-align: center;
  z-index: 2;
  position: relative;
  margin-top: 15rem; /* Added to push content down below the header */

  @media (max-width: 768px) {
    margin-top: 12rem;
  }
`;

const Greeting = styled(motion.div)<{ $isDarkMode: boolean }>`
  font-size: 1.2rem;
  color: ${props => props.$isDarkMode ? '#8b5cf6' : '#7c3aed'};
  margin-bottom: 1rem;
  font-weight: 500;
`;

const Name = styled(motion.h1)`
  font-size: clamp(3rem, 8vw, 6rem);
  font-weight: 800;
  margin: 0;
  background: linear-gradient(135deg, #8b5cf6, #ec4899, #f59e0b);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1.1;

  @media (max-width: 768px) {
    font-size: clamp(2.5rem, 10vw, 4rem);
  }
`;

const Tagline = styled(motion.h2)<{ $isDarkMode: boolean }>`
  font-size: clamp(1.5rem, 4vw, 2.5rem);
  font-weight: 600;
  color: ${props => props.$isDarkMode ? '#ffffff' : '#1e293b'};
  margin: 1rem 0 2rem 0;
  line-height: 1.2;

  /* Force visibility in light mode */
  [data-theme="light"] & {
    color: #1e293b !important;
  }

  @media (max-width: 768px) {
    font-size: clamp(1.25rem, 5vw, 2rem);
    margin: 0.75rem 0 1.5rem 0;
  }
`;

const Description = styled(motion.p)<{ $isDarkMode: boolean }>`
  font-size: 1.2rem;
  color: ${props => props.$isDarkMode ? '#94a3b8' : '#475569'};
  max-width: 600px;
  margin: 0 auto 3rem auto;
  line-height: 1.6;

  @media (max-width: 768px) {
    font-size: 1rem;
    margin: 0 auto 2rem auto;
    padding: 0 1rem;
  }
`;

const CTAButtons = styled(motion.div)`
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 1rem;
    margin-bottom: 2rem;
    padding: 0 1rem;
  }
`;

const PrimaryButton = styled(motion.button)`
  background: linear-gradient(135deg, #8b5cf6, #ec4899);
  color: white;
  border: none;
  padding: 1rem 2rem;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 8px 32px rgba(139, 92, 246, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 40px rgba(139, 92, 246, 0.4);
  }

  @media (max-width: 768px) {
    padding: 0.875rem 1.5rem;
    font-size: 1rem;
  }
`;

const SecondaryButton = styled(motion.button)`
  background: transparent;
  color: #8b5cf6;
  border: 2px solid #8b5cf6;
  padding: 1rem 2rem;
  border-radius: 50px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    background: #8b5cf6;
    color: white;
    transform: translateY(-2px);
  }

  @media (max-width: 768px) {
    padding: 0.875rem 1.5rem;
    font-size: 1rem;
  }
`;

const SocialLinks = styled(motion.div)`
  display: flex;
  gap: 1.5rem;
  justify-content: center;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    gap: 1rem;
    margin-bottom: 1.5rem;
  }
`;

const SocialLink = styled(motion.a)`
  color: #8b5cf6;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: rgba(139, 92, 246, 0.1);
  border: 1px solid rgba(139, 92, 246, 0.2);

  &:hover {
    color: #ec4899;
    background: rgba(139, 92, 246, 0.2);
    border-color: rgba(139, 92, 246, 0.4);
  }

  @media (max-width: 768px) {
    width: 45px;
    height: 45px;
  }
`;

const ScrollIndicator = styled(motion.div)<{ $isDarkMode: boolean }>`
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  color: ${props => props.$isDarkMode ? '#8b5cf6' : '#7c3aed'};
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    color: ${props => props.$isDarkMode ? '#ec4899' : '#db2777'};
  }

  @media (max-width: 768px) {
    bottom: 1rem;
  }
`;

const ScrollText = styled.span`
  font-size: 0.9rem;
  font-weight: 500;

  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const GlowingSwarm = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 1;
  overflow: hidden;
`;

const GlowingDot = styled(motion.div)<{ size: number; color: string }>`
  position: absolute;
  width: ${props => props.size}px;
  height: ${props => props.size}px;
  background: ${props => props.color};
  border-radius: 50%;
  filter: blur(1px);
  box-shadow: 
    0 0 ${props => props.size * 2}px ${props => props.color},
    0 0 ${props => props.size * 4}px ${props => props.color},
    0 0 ${props => props.size * 6}px ${props => props.color};
`;

const Hero: React.FC = () => {
  const { isDarkMode } = useTheme();
  const scrollToAbout = () => {
    const element = document.getElementById('about');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const textVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  // Create 400 glowing dots with varying properties
  const glowingDots = Array.from({ length: 400 }, (_, i) => {
    const colors = [
      'rgba(139, 92, 246, 0.8)',   // Purple
      'rgba(236, 72, 153, 0.8)',   // Pink
      'rgba(245, 158, 11, 0.8)',   // Amber
      'rgba(34, 197, 94, 0.8)',    // Green
      'rgba(59, 130, 246, 0.8)',   // Blue
      'rgba(239, 68, 68, 0.8)',    // Red
    ];
    
    return {
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 4 + 1, // 1-5px
      color: colors[Math.floor(Math.random() * colors.length)],
      delay: Math.random() * 3,
      duration: 4 + Math.random() * 6, // 4-10 seconds
      movementType: Math.floor(Math.random() * 4), // 0-3 different movement patterns
    };
  });

  const getMovementPattern = (type: number) => {
    switch (type) {
      case 0: // Circular motion
        return {
          x: [0, 20, 0, -20, 0],
          y: [0, -20, 0, 20, 0],
        };
      case 1: // Figure 8
        return {
          x: [0, 15, 0, -15, 0],
          y: [0, -10, 0, -10, 0],
        };
      case 2: // Wave motion
        return {
          x: [0, 10, -10, 10, 0],
          y: [0, -15, 0, 15, 0],
        };
      case 3: // Spiral motion
        return {
          x: [0, 8, 16, 8, 0, -8, -16, -8, 0],
          y: [0, -8, 0, 8, 0, -8, 0, 8, 0],
        };
      default:
        return {
          x: [0, 10, 0, -10, 0],
          y: [0, -10, 0, 10, 0],
        };
    }
  };

  return (
    <HeroSection id="hero" $isDarkMode={isDarkMode}>
      <HeroBackground $isDarkMode={isDarkMode} />
      
      <GlowingSwarm>
        {glowingDots.map((dot) => {
          const movement = getMovementPattern(dot.movementType);
          return (
            <GlowingDot
              key={dot.id}
              size={dot.size}
              color={dot.color}
              style={{
                left: `${dot.x}%`,
                top: `${dot.y}%`,
              }}
              animate={{
                ...movement,
                opacity: [0.3, 1, 0.3],
                scale: [0.8, 1.2, 0.8],
              }}
              transition={{
                duration: dot.duration,
                repeat: Infinity,
                delay: dot.delay,
                ease: "easeInOut",
              }}
            />
          );
        })}
      </GlowingSwarm>

      <HeroContent>
        <Greeting
          $isDarkMode={isDarkMode}
          initial="hidden"
          animate="visible"
          variants={textVariants}
          transition={{ duration: 0.6 }}
        >
          Hello, I'm
        </Greeting>

        <Name
          as="h1"
          initial="hidden"
          animate="visible"
          variants={textVariants}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          Briddy...
        </Name>

        <Tagline
          as="h2"
          $isDarkMode={isDarkMode}
          initial="hidden"
          animate="visible"
          variants={textVariants}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="force-visible-text"
        >
          Full-Stack Developer & Tech Enthusiast
        </Tagline>

        <Description
          $isDarkMode={isDarkMode}
          initial="hidden"
          animate="visible"
          variants={textVariants}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          Passionate about creating innovative solutions, building chatbots, 
          and collaborating with talented professionals to bring ideas to life.
        </Description>

        <CTAButtons
          initial="hidden"
          animate="visible"
          variants={textVariants}
          transition={{ duration: 0.6, delay: 0.8 }}
        >
          <PrimaryButton
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => document.getElementById('projects')?.scrollIntoView({ behavior: 'smooth' })}
          >
            View My Work
          </PrimaryButton>
          <SecondaryButton
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
          >
            Let's Collaborate
          </SecondaryButton>
        </CTAButtons>

        <SocialLinks
          initial="hidden"
          animate="visible"
          variants={textVariants}
          transition={{ duration: 0.6, delay: 1.0 }}
        >
          <SocialLink
            href="https://github.com/briddytech"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <FiGithub size={32} />
          </SocialLink>
          <SocialLink
            href="https://linkedin.com/in/briddytech"
            target="_blank"
            rel="noopener noreferrer"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <FiLinkedin size={32} />
          </SocialLink>
          {/* Email icon removed to avoid exposing email */}
          <SocialLink
            href="#contact"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <FiMessageCircle size={32} />
          </SocialLink>
        </SocialLinks>

        {/* Added spacing to prevent overlap with scroll indicator */}
        <div style={{ height: '4rem' }}></div>

        {/* Commented out for future placement consideration
        <div style={{ display: 'flex', alignItems: 'center', fontWeight: 700, fontSize: '2.2rem', letterSpacing: '0.1em', justifyContent: 'center', margin: '2rem 0 1rem 0' }}>
          {'Briddy - The Technocrate'.split('').map((char, i) => (
            <span key={i} style={{
              display: 'inline-block',
              animation: `dance-in 0.5s ${i * 0.08}s both`,
              color: '#8b5cf6',
              marginRight: char === ' ' ? '0.3em' : 0
            }}>{char}</span>
          ))}
        </div>

        <div className="glass-logo">
          <img src="/profile.png" alt="Profile" style={{ width: 120, height: 120, objectFit: 'cover', borderRadius: 16 }} />
        </div>
        */}
      </HeroContent>

      <ScrollIndicator
        $isDarkMode={isDarkMode}
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, delay: 1.2 }}
        onClick={scrollToAbout}
        whileHover={{ scale: 1.1 }}
      >
        <ScrollText>Scroll Down</ScrollText>
        <motion.div
          animate={{ y: [0, 5, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          <FiArrowDown size={20} />
        </motion.div>
      </ScrollIndicator>
    </HeroSection>
  );
};

export default Hero; 